import { useEffect, useState, type ReactNode } from 'react';
import Cropper, { Size } from 'react-easy-crop';
import { useMediaQuery, useTheme } from '@mui/material';
import { DocumentTypeCode, EntityTypeCode } from '@/apiClient';
import { ArrowBackIcon } from '@/components/icons';
import {
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  LoadingButton,
  Slider,
  Typography,
} from '@/primitives';
import {
  ImageDropWrapper,
  PreviewImageWrapper,
  ZoomControlWrapper,
} from './UploadImageModal.styled';
import { messages } from './messages';
import { useImageCropper } from './hooks/useImageCropper';
import {
  UploadCroppedImageResponse,
  useImageUpload,
} from './hooks/useImageUpload';

interface Props {
  cropSize?: Size;
  description?: ReactNode | null;
  documentId?: number;
  documentType: DocumentTypeCode;
  entityId?: string;
  entityType: EntityTypeCode;
  initialFile?: File | null;
  onClose: () => void;
  onUploaded: (response: UploadCroppedImageResponse) => Promise<void> | void;
  open: boolean;
  testId?: string;
  title?: ReactNode;
}

const DEFAULT_CROP_SIZE: Size = {
  width: 200,
  height: 200,
};

export function UploadImageModal({
  cropSize = DEFAULT_CROP_SIZE,
  description,
  documentId,
  documentType,
  entityId,
  entityType,
  initialFile,
  onClose,
  onUploaded,
  open,
  testId,
  title,
}: Props) {
  const {
    previewImage,
    crop,
    setCrop,
    zoom,
    setZoom,
    croppedAreaPixels,
    onCropComplete,
    onSelectImage,
    onReset,
  } = useImageCropper();
  const [image, setImage] = useState<File | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open && initialFile) {
      onSelectImage(initialFile);
      setImage(initialFile);
    }
    // Intentionally omit onSelectImage — it's recreated every render by useImageCropper and would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFile]);

  const { handleSubmit, loadingInProgress } = useImageUpload({
    entityId,
    entityType,
    documentId,
    documentType,
    croppedAreaPixels,
    onUploaded,
    onComplete: onReset,
  });

  const onCloseModal = () => {
    onReset();
    onClose();
  };

  return (
    <Dialog
      data-testid={testId}
      open={open}
      disablePortal
      fullScreen={isMobile}
      fullWidth
      sx={{
        '.MuiDialog-paper': {
          maxHeight: isMobile ? '100%' : '85vh',
          minWidth: isMobile ? 0 : '600px',
          minHeight: isMobile ? 0 : '500px',
          borderRadius: isMobile ? 0 : undefined,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <form
        onSubmit={(e) => handleSubmit(e, image!)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px 24px 16px 8px',
            flexShrink: 0,
          }}
        >
          <IconButton
            aria-label="Back"
            type="button"
            onClick={onCloseModal}
            sx={{ marginRight: '8px' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="headlinesmall"
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mr: 1,
              fontSize: { xs: '20px', sm: '24px' },
            }}
          >
            {title || messages['upload-image-modal.title']}
          </Typography>
          <LoadingButton
            disabled={!previewImage}
            loading={loadingInProgress}
            loadingText="Uploading..."
            type="submit"
            variant="contained"
            sx={{ flexShrink: 0 }}
          >
            Apply
          </LoadingButton>
        </Box>
        <DialogContent
          sx={{
            borderTop: 'none',
            borderBottom: 'none',
            paddingTop: '0px !important',
            paddingBottom: '0px !important',
            paddingLeft: 0,
            paddingRight: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {description != null && description !== '' && (
            <DialogContentText variant="bodymedium" component="div">
              {description}
            </DialogContentText>
          )}
          <ImageDropWrapper cropSize={cropSize}>
            {previewImage && (
              <>
                <PreviewImageWrapper cropSize={cropSize}>
                  <Cropper
                    image={previewImage}
                    crop={crop}
                    aspect={cropSize.width / cropSize.height}
                    zoom={zoom}
                    zoomSpeed={0.1}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    objectFit="contain"
                  />
                </PreviewImageWrapper>
                <ZoomControlWrapper width={cropSize.width}>
                  <Slider
                    size="small"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    aria-label="Zoom"
                    valueLabelDisplay="auto"
                    onChange={(_event: Event, newValue: number | number[]) => {
                      setZoom(newValue as number);
                    }}
                    sx={{ color: 'common.white' }}
                  />
                </ZoomControlWrapper>
              </>
            )}
          </ImageDropWrapper>
        </DialogContent>
      </form>
    </Dialog>
  );
}
